import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import UserBaseUtils from "Utility/UserBase.js";
import AxiosWidgetChurch from "components/WidgetChurch/Axios";
import StorageKeys from "Utility/StorageKey.js";
import StorageBox from "Store/StorageBox.js";
import Dropdown from "components/dropdown";

const CHURCH_STORAGE_KEY = "current_church";

const ChurchSelector = () => {
  const [churches, setChurches] = useState<any[]>([]);
  const [selectedChurch, setSelectedChurch] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const processChurchesData = (churchesData: any[]) => {
    if (churchesData && churchesData.length > 0) {
      setChurches(churchesData);

      // Check if there's a stored selected church
      const storedChurchId = localStorage.getItem(CHURCH_STORAGE_KEY);
      if (storedChurchId) {
        const stored = churchesData.find((c: any) => c.id === storedChurchId);
        if (stored) {
          setSelectedChurch(stored);
        } else {
          // If stored church not found, select first one
          setSelectedChurch(churchesData[0]);
          localStorage.setItem(CHURCH_STORAGE_KEY, churchesData[0].id);
        }
      } else {
        // No stored church, select first one
        setSelectedChurch(churchesData[0]);
        localStorage.setItem(CHURCH_STORAGE_KEY, churchesData[0].id);
      }
    }
  };

  useEffect(() => {
    const loadChurches = async () => {
      try {
        // First, try to get churches from StorageBox (loaded by CreateChurchTable)
        const storedChurches = StorageBox.getChurchesArray();

        if (storedChurches && storedChurches.length > 0) {
          console.log(
            "Using churches data from StorageBox (loaded by CreateChurchTable)"
          );
          processChurchesData(storedChurches);
          setLoading(false);
          return;
        }

        // If not found in StorageBox, load from API
        console.log("Churches not found in StorageBox, loading from API");
        const user = UserBaseUtils.getProfileUserName();
        const response = await AxiosWidgetChurch.getChurch(user);

        if (response.data && response.data.length > 0) {
          processChurchesData(response.data);
        }
      } catch (error) {
        console.error("Error loading churches:", error);
      } finally {
        setLoading(false);
      }
    };

    loadChurches();

    // Listen for church data updates from CreateChurchTable
    const handleChurchDataUpdated = (event: CustomEvent) => {
      const updatedChurches = event.detail?.churches;
      if (updatedChurches && updatedChurches.length > 0) {
        console.log(
          "Received church data update event, refreshing churches list"
        );
        processChurchesData(updatedChurches);
      } else {
        // If no detail, try to get from StorageBox
        const storedChurches = StorageBox.getChurchesArray();
        if (storedChurches && storedChurches.length > 0) {
          processChurchesData(storedChurches);
        }
      }
    };

    window.addEventListener(
      "churchDataUpdated",
      handleChurchDataUpdated as EventListener
    );

    return () => {
      window.removeEventListener(
        "churchDataUpdated",
        handleChurchDataUpdated as EventListener
      );
    };
  }, []);

  const handleChurchSelect = (church: any) => {
    setSelectedChurch(church);
    localStorage.setItem(CHURCH_STORAGE_KEY, church.id);
  };

  const getChurchImageUrl = (imageName: string) => {
    if (!imageName) return null;
    return `${StorageKeys.SupabaseChurchUrlProfile}${imageName}`;
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  // No churches - show button to create one
  if (churches.length === 0) {
    return (
      <Link
        to="/admin/church"
        className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-orange-600 dark:bg-navy-700 dark:text-white"
      >
        <span>Create Your Church</span>
      </Link>
    );
  }

  // Single church - show with remark
  if (churches.length === 1) {
    const church = churches[0];
    return (
      <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 dark:bg-navy-700">
        <div className="flex flex-col">
          <span className="text-md font-semibold text-gray-700 dark:text-white">
            {church.name}
          </span>
          <span className="text-[10px] text-gray-500 dark:text-gray-400">
            currently managing
          </span>
        </div>
      </div>
    );
  }

  // Multiple churches - show dropdown
  const imageUrl = selectedChurch
    ? getChurchImageUrl(selectedChurch.imagesName)
    : null;

  return (
    <Dropdown
      button={
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 dark:bg-navy-700">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={selectedChurch?.name || "Church"}
              className="h-6 w-6 rounded-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-gray-700 dark:text-white">
              {selectedChurch?.name || "Select Church"}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              currently managing
            </span>
          </div>
        </div>
      }
      classNames="py-2 top-12 right-0 w-64 bg-white dark:bg-navy-800 rounded-lg shadow-xl"
      children={
        <div className="max-h-64 overflow-y-auto">
          {churches.map((church) => {
            const churchImageUrl = getChurchImageUrl(church.imagesName);
            const isSelected = selectedChurch?.id === church.id;

            return (
              <div
                key={church.id}
                onClick={() => handleChurchSelect(church)}
                className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors ${
                  isSelected
                    ? "bg-blue-50 dark:bg-navy-700"
                    : "hover:bg-gray-50 dark:hover:bg-navy-700"
                }`}
              >
                {churchImageUrl && (
                  <img
                    src={churchImageUrl}
                    alt={church.name}
                    className="h-8 w-8 rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                <div className="flex flex-1 flex-col">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {church.name}
                  </span>
                  {church.introduction && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {church.introduction}
                    </span>
                  )}
                </div>
                {isSelected && (
                  <svg
                    className="h-5 w-5 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            );
          })}
          <div className="border-t border-gray-200 dark:border-navy-600">
            <Link
              to="/admin/church"
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-orange-600 hover:bg-gray-50 dark:text-blue-400 dark:hover:bg-navy-700"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create New Church
            </Link>
          </div>
        </div>
      }
    />
  );
};

export default ChurchSelector;
